// ============================================================
// ExpressJwtRefreshFlow.jsx
//
// A branded, responsive course content page for the
// "JWT Refresh Token Flow" lesson.
//
// Author: ProDev Security Series
// Features: Dual-token strategy (Access/Refresh), token
//           revocation logic, and secure cookie management.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import SyncIcon               from "@mui/icons-material/Sync";
import VpnKeyIcon             from "@mui/icons-material/VpnKey";
import LogoutIcon             from "@mui/icons-material/Logout";
import ShieldIcon             from "@mui/icons-material/Shield";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import CookieIcon             from "@mui/icons-material/Cookie";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressJwtRefreshFlow() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Security · Module 14"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Full <span>Refresh Token Flow</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        A dual-token strategy balances security and user experience. Short-lived Access Tokens minimize the window for theft, while long-lived Refresh Tokens allow for seamless session renewal.
                    </Typography>
                </Box>

                {/* 1. INITIALIZATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Dual-Secret Setup" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        You need two distinct secrets: one for short-term access and one for long-term renewal.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const ACCESS_SECRET = process.env.ACCESS_SECRET;", comment: "15 min expiry" },
                        { cmd: "const REFRESH_SECRET = process.env.REFRESH_SECRET;", comment: "7 day expiry" },
                        { cmd: "let refreshTokenStore = [];", comment: "In Prod, use Redis for revocation" },
                    ]} />
                </Box>

                {/* 2. LOGIN FLOW */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<VpnKeyIcon />} label="2. Issuing the Token Pair" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        At login, return the access token in JSON and the refresh token in a secure, <code style={{color: BRAND.white}}>httpOnly</code> cookie.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.post('/login', (req, res) => {", comment: "" },
                        { cmd: "  const accessToken = jwt.sign(user, ACCESS_SECRET, { expiresIn: '15m' });", comment: "" },
                        { cmd: "  const refreshToken = jwt.sign(user, REFRESH_SECRET, { expiresIn: '7d' });", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "  refreshTokenStore.push(refreshToken);", comment: "Whitelist the token" },
                        { cmd: "  res.cookie('refreshToken', refreshToken, { httpOnly: true, secure: true });", comment: "Secure cookie" },
                        { cmd: "  res.json({ accessToken });", comment: "Send access token to frontend memory" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 3. REFRESH LOGIC */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SyncIcon />} label="3. The Silent Refresh" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        When the Access Token expires, the client calls this endpoint to get a fresh one without requiring the user to re-type their password.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.post('/token', (req, res) => {", comment: "" },
                        { cmd: "  const token = req.cookies.refreshToken;", comment: "Fetch from cookie" },
                        { cmd: "  if (!token || !refreshTokenStore.includes(token)) return res.sendStatus(403);", comment: "Verify whitelist" },
                        { cmd: "", comment: "" },
                        { cmd: "  jwt.verify(token, REFRESH_SECRET, (err, user) => {", comment: "Verify signature" },
                        { cmd: "    if (err) return res.sendStatus(403);", comment: "" },
                        { cmd: "    const newAccess = jwt.sign({ id: user.id }, ACCESS_SECRET, { expiresIn: '15m' });", comment: "" },
                        { cmd: "    res.json({ accessToken: newAccess });", comment: "" },
                        { cmd: "  });", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 4. LOGOUT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LogoutIcon />} label="4. Revocation (Logout)" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Logout must clear the cookie <strong>and</strong> invalidate the refresh token in your store to prevent reuse.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.post('/logout', (req, res) => {", comment: "" },
                        { cmd: "  const token = req.cookies.refreshToken;", comment: "" },
                        { cmd: "  refreshTokenStore = refreshTokenStore.filter(t => t !== token);", comment: "Remove from whitelist" },
                        { cmd: "  res.clearCookie('refreshToken');", comment: "Delete client cookie" },
                        { cmd: "  res.sendStatus(204);", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES GRID */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ShieldIcon />} label="Security Hardening" />
                    <Grid container spacing={2}>
                        {[
                            { title: "HttpOnly Cookies", desc: "Prevents XSS attacks from reading the refresh token.", icon: <CookieIcon /> },
                            { title: "Token Rotation", desc: "Issue a new Refresh Token on every renewal to detect reuse.", icon: <SyncIcon /> },
                            { title: "Database Persistence", desc: "Use Redis or MongoDB to store tokens for horizontal scaling.", icon: <CodeIcon /> },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Box>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Never store the Access Token in localStorage; it's vulnerable to XSS. Keep it in <strong>JavaScript memory</strong> (a variable in your React state) and use the Refresh Token to get a new one if the user refreshes the page.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
